import { useEffect, useMemo, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Megaphone,
  Plus,
  Play,
  Pause,
  Trash,
  DownloadSimple,
  CaretLeft,
  CheckCircle,
  Checks,
  XCircle,
  Clock,
  CircleNotch,
  UploadSimple,
  FileCsv,
} from "@phosphor-icons/react";
import { formatDistanceToNow } from "date-fns";

const STATUS_COLOR = {
  draft: "bg-[#E5E7EB] text-[#374151]",
  running: "bg-[#DBEAFE] text-[#1E40AF]",
  paused: "bg-[#FEF3C7] text-[#92400E]",
  completed: "bg-[#D1FAE5] text-[#065F46]",
  failed: "bg-[#FEE2E2] text-[#991B1B]",
  pending: "bg-[#FEF3C7] text-[#92400E]",
  sent: "bg-[#D1FAE5] text-[#065F46]",
  delivered: "bg-[#E0F2FE] text-[#075985]",
  read: "bg-[#DBEAFE] text-[#1E40AF]",
};

function StatusPill({ status }) {
  return (
    <Badge
      className={`${STATUS_COLOR[status] || STATUS_COLOR.draft} hover:${STATUS_COLOR[status]} text-[10px] uppercase tracking-wider px-2 py-0.5`}
    >
      {status}
    </Badge>
  );
}

function ProgressBar({ total, sent, failed }) {
  const sentPct = total ? (sent / total) * 100 : 0;
  const failedPct = total ? (failed / total) * 100 : 0;
  return (
    <div className="h-2 w-full bg-[var(--bg)] rounded-full overflow-hidden flex">
      <div className="bg-[#10B981]" style={{ width: `${sentPct}%` }} />
      <div className="bg-[#EF4444]" style={{ width: `${failedPct}%` }} />
    </div>
  );
}

function CampaignList({ items, onOpen, onCreate, onRefresh, refreshing }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="heading text-lg font-semibold">Campaigns</div>
          <div className="text-xs text-[var(--muted)]">
            Bulk WhatsApp template campaigns. Upload a CSV → run → track analytics.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={refreshing}
            data-testid="campaigns-refresh-btn"
          >
            {refreshing ? <CircleNotch size={14} className="animate-spin" /> : "Refresh"}
          </Button>
          <Button
            size="sm"
            onClick={onCreate}
            className="bg-[#25D366] hover:bg-[#1da851] text-white gap-1.5"
            data-testid="campaign-new-btn"
          >
            <Plus size={14} weight="bold" /> New Campaign
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] rounded-lg p-10 text-center text-sm text-[var(--muted)]">
          <Megaphone size={32} className="mx-auto mb-3 opacity-40" />
          No campaigns yet. Click <b>New Campaign</b> to send your first bulk message.
        </div>
      ) : (
        <ul className="space-y-2 max-h-[55vh] overflow-y-auto scroll-thin pr-1">
          {items.map((c) => (
            <li
              key={c.id}
              className="border border-[var(--border)] rounded-lg p-3 hover:bg-[var(--bg)] cursor-pointer transition-colors"
              onClick={() => onOpen(c.id)}
              data-testid={`campaign-row-${c.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm truncate">{c.name}</span>
                    <StatusPill status={c.status} />
                  </div>
                  <div className="text-[11px] text-[var(--muted)] mt-0.5 flex items-center gap-2">
                    <span>📋 {c.template_name}</span>
                    <span>·</span>
                    <span>{c.total} recipients</span>
                    {c.created_at && (
                      <>
                        <span>·</span>
                        <span>{formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs flex items-center gap-2">
                    <span className="text-[#065F46]" title="Sent">✓ {c.sent}</span>
                    <span className="text-[#0EA5E9]" title="Delivered">✓✓ {c.delivered || 0}</span>
                    <span className="text-[#3B82F6]" title="Read">👁 {c.read || 0}</span>
                    <span className="text-[#991B1B]" title="Failed">✗ {c.failed}</span>
                  </div>
                </div>
              </div>
              <div className="mt-2">
                <ProgressBar total={c.total} sent={c.sent} failed={c.failed} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function WhatsAppPreview({ tpl, tplStructure, variableMap, csvHeaders, csvFile, headerPreviewUrl }) {
  // Sample row = first CSV row (for live preview)
  const [sampleRow, setSampleRow] = useState(null);
  useEffect(() => {
    if (!csvFile) {
      setSampleRow(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result || "";
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) return setSampleRow(null);
      const parser = (s) => s.split(",").map((c) => c.trim());
      const headers = parser(lines[0]).map((h) => h.toLowerCase());
      const row = parser(lines[1]);
      const obj = {};
      headers.forEach((h, i) => { obj[h] = row[i] || ""; });
      setSampleRow(obj);
    };
    reader.readAsText(csvFile);
  }, [csvFile]);

  if (!tplStructure) {
    return (
      <div className="text-sm text-[var(--muted)] text-center py-10">
        Select a template to see preview
      </div>
    );
  }

  // Render body with values substituted
  const renderedBody = (tplStructure.bodyText || "").replace(/\{\{(\d+)\}\}/g, (_, n) => {
    const idx = parseInt(n, 10) - 1;
    const vm = variableMap[idx];
    if (!vm) return `{{${n}}}`;
    if (vm.mode === "static") return vm.value || `{{${n}}}`;
    if (vm.mode === "csv") {
      const v = sampleRow?.[(vm.column || "").toLowerCase()];
      return v || `[${vm.column || "CSV"}]`;
    }
    if (vm.mode === "name") return sampleRow?.name || "[name]";
    if (vm.mode === "phone") return sampleRow?.phone || "[phone]";
    return `{{${n}}}`;
  });

  // WhatsApp formatting: *bold*, _italic_
  const formatWA = (text) => {
    // simple bold support
    const parts = text.split(/(\*[^*\n]+\*)/g);
    return parts.map((p, i) => {
      if (/^\*[^*\n]+\*$/.test(p)) {
        return <b key={i}>{p.slice(1, -1)}</b>;
      }
      return <span key={i}>{p}</span>;
    });
  };

  const header = tplStructure.header;

  return (
    <div className="h-full flex flex-col">
      <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold mb-2">
        Preview (sample row)
      </div>
      {/* Phone frame */}
      <div className="flex-1 rounded-2xl border border-[var(--border)] bg-[#ECE5DD] p-3 overflow-y-auto scroll-thin">
        <div className="flex justify-start">
          <div className="max-w-[90%] bg-white rounded-lg rounded-tl-sm shadow-sm overflow-hidden">
            {/* Header */}
            {header?.format === "IMAGE" && (
              <div className="bg-[#E5E7EB] aspect-video flex items-center justify-center overflow-hidden">
                {headerPreviewUrl ? (
                  <img src={headerPreviewUrl} alt="header" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-[11px] text-[var(--muted)]">Image placeholder</span>
                )}
              </div>
            )}
            {header?.format === "VIDEO" && (
              <div className="bg-[#1F2937] aspect-video flex items-center justify-center">
                {headerPreviewUrl ? (
                  <video src={headerPreviewUrl} className="w-full h-full" muted />
                ) : (
                  <span className="text-[11px] text-white/60">Video placeholder</span>
                )}
              </div>
            )}
            {header?.format === "DOCUMENT" && (
              <div className="bg-[#F3F4F6] p-3 flex items-center gap-2 border-b border-[var(--border)]">
                <FileCsv size={18} className="text-[#DC2626]" />
                <span className="text-xs font-medium truncate">{headerPreviewUrl ? "document.pdf" : "Document"}</span>
              </div>
            )}
            {header?.format === "TEXT" && header.text && (
              <div className="px-3 pt-2.5 font-bold text-sm text-[#111827]">{header.text}</div>
            )}
            {/* Body */}
            <div className="px-3 py-2.5 text-sm text-[#111827] whitespace-pre-wrap leading-relaxed">
              {formatWA(renderedBody)}
            </div>
            {/* Footer */}
            {tplStructure.footer && (
              <div className="px-3 pb-2 text-[11px] text-[#6B7280]">{tplStructure.footer}</div>
            )}
            {/* Buttons */}
            {tplStructure.buttons?.length > 0 && (
              <div className="border-t border-[var(--border)] divide-y divide-[var(--border)]">
                {tplStructure.buttons.map((b, i) => (
                  <div key={i} className="px-3 py-2 text-xs text-center text-[#2563EB] font-medium">
                    {b.text || b.url || "Button"}
                  </div>
                ))}
              </div>
            )}
            {/* Time tick */}
            <div className="px-3 pb-1.5 flex justify-end">
              <span className="text-[10px] text-[#9CA3AF]">12:34 PM</span>
            </div>
          </div>
        </div>
        {sampleRow && (
          <div className="mt-3 text-[10px] text-[var(--muted)] text-center">
            Showing values from row 1: +{sampleRow.phone} {sampleRow.name && `· ${sampleRow.name}`}
          </div>
        )}
      </div>
    </div>
  );
}


function CreateForm({ onBack, onCreated }) {
  const [name, setName] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRowCount, setCsvRowCount] = useState(0);
  const [headerFile, setHeaderFile] = useState(null);
  const [headerMediaId, setHeaderMediaId] = useState("");
  const [headerPreviewUrl, setHeaderPreviewUrl] = useState(null);
  const [headerUploading, setHeaderUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [variableMap, setVariableMap] = useState([]); // [{mode, value, column}]
  const fileRef = useRef(null);
  const headerFileRef = useRef(null);

  useEffect(() => {
    api
      .get("/whatsapp/templates")
      .then((r) => setTemplates((r.data.templates || []).filter((t) => t.status === "APPROVED")))
      .catch(() => setTemplates([]));
  }, []);

  const tpl = templates.find((t) => `${t.name}:${t.language}` === selected);

  // Parse template structure
  const tplStructure = useMemo(() => {
    if (!tpl) return null;
    let header = null;
    let bodyText = "";
    let footer = null;
    let buttons = [];
    for (const c of tpl.components || []) {
      const type = (c.type || "").toUpperCase();
      if (type === "HEADER") {
        const fmt = (c.format || "TEXT").toUpperCase();
        header = {
          format: fmt,
          text: c.text || null,
          requiresMedia: ["IMAGE", "VIDEO", "DOCUMENT"].includes(fmt),
        };
      } else if (type === "BODY") {
        bodyText = c.text || "";
      } else if (type === "FOOTER") {
        footer = c.text || null;
      } else if (type === "BUTTONS") {
        buttons = c.buttons || [];
      }
    }
    const varMatches = [...bodyText.matchAll(/\{\{(\d+)\}\}/g)];
    const varNumbers = [...new Set(varMatches.map((m) => parseInt(m[1], 10)))].sort((a, b) => a - b);
    return { header, bodyText, footer, buttons, varCount: varNumbers.length, varNumbers };
  }, [tpl]);

  // Reset header media + variable map when template changes
  useEffect(() => {
    setHeaderFile(null);
    setHeaderMediaId("");
    if (headerPreviewUrl) URL.revokeObjectURL(headerPreviewUrl);
    setHeaderPreviewUrl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // When template structure or CSV columns change, reset variable map with sensible defaults
  useEffect(() => {
    if (!tplStructure) {
      setVariableMap([]);
      return;
    }
    const paramCols = csvHeaders.filter((h) => h !== "phone" && h !== "name");
    const next = Array.from({ length: tplStructure.varCount }, (_, i) => {
      const col = paramCols[i];
      if (col) return { mode: "csv", column: col, value: "" };
      return { mode: "static", column: "", value: "" };
    });
    setVariableMap(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tplStructure?.varCount, csvHeaders.join("|")]);

  // Parse CSV preview when file changes
  useEffect(() => {
    if (!csvFile) {
      setCsvHeaders([]);
      setCsvRowCount(0);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result || "";
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length === 0) return;
      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
      setCsvHeaders(headers);
      setCsvRowCount(Math.max(0, lines.length - 1));
    };
    reader.readAsText(csvFile);
  }, [csvFile]);

  // Compute CSV→variable mapping
  const paramColumns = useMemo(() => {
    return csvHeaders.filter((h) => h !== "phone" && h !== "name");
  }, [csvHeaders]);

  // Upload header media
  const uploadHeader = async (file) => {
    setHeaderUploading(true);
    setHeaderFile(file);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("wh_token");
      const res = await fetch(`${API_BASE}/media/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        let msg = "Upload failed";
        try {
          const err = await res.json();
          const d = err.detail;
          if (typeof d === "string") msg = d;
          else if (d?.message) msg = `${d.message}${d.code ? ` (code ${d.code})` : ""}`;
          else msg = JSON.stringify(d).slice(0, 200);
        } catch (_) {}
        throw new Error(msg);
      }
      const data = await res.json();
      setHeaderMediaId(data.media_id);
      // Create local preview URL
      const url = URL.createObjectURL(file);
      setHeaderPreviewUrl(url);
      toast.success("Header media uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
      setHeaderFile(null);
    } finally {
      setHeaderUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Campaign name daaliye");
    if (!tpl) return toast.error("Please select a template");
    if (!csvFile) return toast.error("CSV file upload karein");
    if (tplStructure?.header?.requiresMedia && !headerMediaId) {
      return toast.error(`Template requires a ${tplStructure.header.format.toLowerCase()} header — upload karein`);
    }
    if (tplStructure && tplStructure.varCount > 0) {
      for (let i = 0; i < tplStructure.varCount; i++) {
        const vm = variableMap[i] || {};
        if (vm.mode === "static") {
          if (!String(vm.value || "").trim()) {
            return toast.error(`Variable {{${i + 1}}} ke liye value daaliye`);
          }
        } else if (vm.mode === "csv") {
          if (!vm.column || !csvHeaders.includes(vm.column)) {
            return toast.error(`Please select a CSV column for variable {{${i + 1}}}`);
          }
        }
      }
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("template_name", tpl.name);
      fd.append("language", tpl.language);
      if (headerMediaId && tplStructure?.header?.requiresMedia) {
        fd.append("header_kind", tplStructure.header.format.toLowerCase());
        fd.append("header_media_id", headerMediaId);
      }
      if (variableMap.length > 0) {
        fd.append("variable_map", JSON.stringify(variableMap));
      }
      fd.append("csv_file", csvFile);
      const token = localStorage.getItem("wh_token");
      const res = await fetch(`${API_BASE}/campaigns`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Create failed");
      }
      const data = await res.json();
      toast.success(`Campaign created — ${data.total} recipients ready`);
      onCreated(data.id);
    } catch (err) {
      toast.error(err.message || "Failed to create campaign");
    } finally {
      setSubmitting(false);
    }
  };

  const headerAccept =
    tplStructure?.header?.format === "IMAGE" ? "image/*" :
    tplStructure?.header?.format === "VIDEO" ? "video/*" :
    tplStructure?.header?.format === "DOCUMENT" ? ".pdf,application/pdf" :
    "*/*";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4" data-testid="campaign-create-layout">
      <form onSubmit={submit} className="space-y-4" data-testid="campaign-create-form">
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="icon" onClick={onBack}>
          <CaretLeft size={18} />
        </Button>
        <div>
          <div className="heading text-lg font-semibold">New Campaign</div>
          <div className="text-xs text-[var(--muted)]">Upload a CSV with phone numbers and template parameters.</div>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="camp-name">Campaign name</Label>
        <Input
          id="camp-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. April booking blast"
          data-testid="campaign-name-input"
        />
      </div>

      <div className="space-y-1.5">
        <Label>Template (only APPROVED templates)</Label>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="w-full h-10 px-3 rounded-md border border-[var(--border)] bg-[var(--bg)] text-sm"
          data-testid="campaign-template-select"
        >
          <option value="">— Select template —</option>
          {templates.map((t) => (
            <option key={`${t.name}:${t.language}`} value={`${t.name}:${t.language}`}>
              {t.name} ({t.language}) · {t.category}
            </option>
          ))}
        </select>
      </div>

      {/* Template preview after selection */}
      {tplStructure && (
        <div className="rounded-lg border border-[var(--border)] bg-[var(--bg)] p-3 space-y-2.5" data-testid="template-preview">
          <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">Template structure</div>

          {/* Header */}
          {tplStructure.header && (
            <div>
              <div className="text-[10px] text-[var(--muted)] uppercase">Header · {tplStructure.header.format}</div>
              {tplStructure.header.format === "TEXT" ? (
                <div className="text-sm font-bold mt-0.5">{tplStructure.header.text}</div>
              ) : (
                <div className="mt-1.5">
                  <input
                    ref={headerFileRef}
                    type="file"
                    accept={headerAccept}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadHeader(f);
                      e.target.value = "";
                    }}
                    className="hidden"
                    data-testid="header-media-input"
                  />
                  <button
                    type="button"
                    onClick={() => headerFileRef.current?.click()}
                    disabled={headerUploading}
                    className="w-full border border-dashed border-[var(--border)] rounded-md p-3 text-xs hover:bg-[var(--surface)] flex items-center justify-center gap-2"
                    data-testid="header-media-button"
                  >
                    {headerUploading ? (
                      <><CircleNotch size={14} className="animate-spin" /> Uploading…</>
                    ) : headerMediaId ? (
                      <>
                        <CheckCircle size={14} weight="fill" className="text-[#10B981]" />
                        <span className="font-medium">{headerFile?.name || "Uploaded"}</span>
                        <span className="text-[var(--muted)]">— click to replace</span>
                      </>
                    ) : (
                      <>
                        <UploadSimple size={14} />
                        Upload {tplStructure.header.format.toLowerCase()} (sent to all recipients)
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Body */}
          {tplStructure.bodyText && (
            <div>
              <div className="text-[10px] text-[var(--muted)] uppercase">Body</div>
              <div className="text-sm mt-0.5 whitespace-pre-wrap leading-relaxed">
                {tplStructure.bodyText.split(/(\{\{\d+\}\})/).map((part, i) => {
                  const m = part.match(/^\{\{(\d+)\}\}$/);
                  if (!m) return <span key={i}>{part}</span>;
                  const idx = parseInt(m[1], 10);
                  const vm = variableMap[idx - 1];
                  let label = `{{${idx}}}`;
                  let tone = "bg-[#EF4444]/15 text-[#991B1B] border-[#EF4444]/40";
                  if (vm?.mode === "static" && String(vm.value || "").trim()) {
                    label = `${idx}: ${vm.value}`;
                    tone = "bg-[#3B82F6]/15 text-[#1E40AF] border-[#3B82F6]/40";
                  } else if (vm?.mode === "csv" && vm.column) {
                    label = `${idx}: ${vm.column}`;
                    tone = "bg-[#10B981]/15 text-[#065F46] border-[#10B981]/40";
                  } else if (vm?.mode === "name") {
                    label = `${idx}: name`;
                    tone = "bg-[#10B981]/15 text-[#065F46] border-[#10B981]/40";
                  } else if (vm?.mode === "phone") {
                    label = `${idx}: phone`;
                    tone = "bg-[#10B981]/15 text-[#065F46] border-[#10B981]/40";
                  }
                  return (
                    <span
                      key={i}
                      className={`inline-block px-1.5 py-0.5 mx-0.5 rounded text-[11px] font-mono font-semibold border ${tone}`}
                    >
                      {label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Per-variable editor */}
          {tplStructure.varCount > 0 && (
            <div className="space-y-2 pt-1 border-t border-[var(--border)]">
              <div className="text-[10px] text-[var(--muted)] uppercase">Fill body variables</div>
              {Array.from({ length: tplStructure.varCount }).map((_, i) => {
                const vm = variableMap[i] || { mode: "static", value: "", column: "" };
                const setVm = (patch) => {
                  setVariableMap((prev) => {
                    const next = [...prev];
                    next[i] = { ...vm, ...patch };
                    return next;
                  });
                };
                const colOptions = csvHeaders.filter((h) => h !== "phone");
                return (
                  <div
                    key={i}
                    className="grid grid-cols-12 gap-2 items-center"
                    data-testid={`variable-row-${i + 1}`}
                  >
                    <div className="col-span-2 text-xs font-mono font-bold text-[var(--fg)]">
                      {`{{${i + 1}}}`}
                    </div>
                    <select
                      value={vm.mode}
                      onChange={(e) => {
                        const mode = e.target.value;
                        if (mode === "csv") {
                          const auto = csvHeaders.filter((h) => h !== "phone" && h !== "name")[i] || "";
                          setVm({ mode, column: auto, value: "" });
                        } else if (mode === "name") {
                          setVm({ mode, column: "", value: "" });
                        } else if (mode === "phone") {
                          setVm({ mode, column: "", value: "" });
                        } else {
                          setVm({ mode: "static", column: "", value: vm.value || "" });
                        }
                      }}
                      className="col-span-3 h-9 px-2 rounded-md border border-[var(--border)] bg-[var(--surface)] text-xs"
                      data-testid={`variable-mode-${i + 1}`}
                    >
                      <option value="static">Static value</option>
                      <option value="csv">CSV column</option>
                      <option value="name">Customer name</option>
                      <option value="phone">Phone</option>
                    </select>
                    <div className="col-span-7">
                      {vm.mode === "static" ? (
                        <Input
                          value={vm.value || ""}
                          onChange={(e) => setVm({ value: e.target.value })}
                          placeholder={`Value for {{${i + 1}}} (sent to all)`}
                          className="h-9 text-xs bg-[var(--surface)]"
                          data-testid={`variable-static-${i + 1}`}
                        />
                      ) : vm.mode === "csv" ? (
                        <select
                          value={vm.column || ""}
                          onChange={(e) => setVm({ column: e.target.value })}
                          className="w-full h-9 px-2 rounded-md border border-[var(--border)] bg-[var(--surface)] text-xs"
                          data-testid={`variable-csv-${i + 1}`}
                          disabled={colOptions.length === 0}
                        >
                          <option value="">— Select column —</option>
                          {colOptions.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      ) : (
                        <div className="text-[11px] text-[var(--muted)] italic h-9 flex items-center">
                          Auto-filled from CSV {vm.mode} column
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer */}
          {tplStructure.footer && (
            <div>
              <div className="text-[10px] text-[var(--muted)] uppercase">Footer</div>
              <div className="text-[11px] text-[var(--muted)] mt-0.5">{tplStructure.footer}</div>
            </div>
          )}

          {/* Variable summary */}
          <div className="text-[11px] flex flex-wrap items-center gap-2 pt-1 border-t border-[var(--border)]">
            <span className="text-[var(--muted)]">Variables:</span>
            <Badge variant="outline" className="text-[10px]">{tplStructure.varCount} body</Badge>
            {tplStructure.header?.requiresMedia && (
              <Badge variant="outline" className="text-[10px]">
                1 {tplStructure.header.format.toLowerCase()} header
              </Badge>
            )}
            {csvHeaders.length > 0 && (
              <span className="text-[var(--muted)] ml-auto">
                CSV: {csvHeaders.length} column{csvHeaders.length === 1 ? "" : "s"} · {csvRowCount} rows
              </span>
            )}
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <Label>CSV file</Label>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
          className="hidden"
          data-testid="campaign-csv-input"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="w-full border border-dashed border-[var(--border)] rounded-md p-4 text-sm hover:bg-[var(--bg)] flex items-center justify-center gap-2"
        >
          {csvFile ? (
            <>
              <FileCsv size={18} className="text-[#10B981]" />
              <span className="font-medium">{csvFile.name}</span>
              <span className="text-[var(--muted)]">
                — {csvRowCount} rows · click to change
              </span>
            </>
          ) : (
            <>
              <UploadSimple size={18} />
              <span>Click to upload CSV</span>
            </>
          )}
        </button>
        {csvHeaders.length > 0 && (
          <div className="text-[11px] text-[var(--muted)]">
            Detected columns: {csvHeaders.map((h, i) => (
              <code key={i} className="font-mono mx-0.5 px-1 py-0.5 rounded bg-[var(--bg)] border border-[var(--border)]">
                {h}
              </code>
            ))}
          </div>
        )}
        <div className="text-[11px] text-[var(--muted)] leading-relaxed">
          CSV format: first row = headers. Required column: <code className="font-mono">phone</code> (E.164 without +).
          Optional: <code className="font-mono">name</code>. Other columns become template body parameters in order ({"{{1}}, {{2}}…"}).
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onBack}>Cancel</Button>
        <Button
          type="submit"
          disabled={submitting}
          className="bg-[#25D366] hover:bg-[#1da851] text-white gap-1.5"
          data-testid="campaign-create-submit"
        >
          {submitting ? <CircleNotch size={14} className="animate-spin" /> : <Plus size={14} weight="bold" />}
          Create campaign
        </Button>
      </div>
    </form>

    <aside className="hidden lg:block sticky top-0 self-start">
      <WhatsAppPreview
        tpl={tpl}
        tplStructure={tplStructure}
        variableMap={variableMap}
        csvHeaders={csvHeaders}
        csvFile={csvFile}
        headerPreviewUrl={headerPreviewUrl}
      />
    </aside>
    </div>
  );
}

function CampaignDetail({ id, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const refresh = async () => {
    try {
      const r = await api.get(`/campaigns/${id}`);
      setData(r.data);
    } catch (_) {
      toast.error("Failed to load campaign");
    }
  };

  useEffect(() => {
    refresh();
  }, [id]);

  // Auto-refresh while running
  useEffect(() => {
    if (!data || data.status !== "running") return;
    const t = setInterval(refresh, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.status]);

  const action = async (verb) => {
    setActionLoading(true);
    try {
      await api.post(`/campaigns/${id}/${verb}`);
      toast.success(`Campaign ${verb}ed`);
      onChanged();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${verb} failed`);
    } finally {
      setActionLoading(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete campaign "${data?.name}"? This cannot be undone.`)) return;
    setActionLoading(true);
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success("Campaign deleted");
      onChanged();
      onBack();
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setActionLoading(false);
    }
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("wh_token");
      const res = await fetch(`${API_BASE}/campaigns/${id}/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `campaign_${data?.name || id}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) {
      toast.error("Export failed");
    }
  };

  const filteredRecipients = useMemo(() => {
    if (!data?.recipients) return [];
    if (filter === "all") return data.recipients;
    return data.recipients.filter((r) => r.status === filter);
  }, [data, filter]);

  if (!data) {
    return (
      <div className="text-center py-10 text-sm text-[var(--muted)]">
        <CircleNotch size={20} className="animate-spin inline-block" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="icon" onClick={onBack}>
          <CaretLeft size={18} />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="heading text-lg font-semibold truncate">{data.name}</div>
            <StatusPill status={data.status} />
          </div>
          <div className="text-xs text-[var(--muted)]">
            Template: <code className="font-mono">{data.template_name}</code> ({data.language})
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {data.status === "draft" && (
            <Button size="sm" onClick={() => action("start")} disabled={actionLoading}
              className="bg-[#25D366] hover:bg-[#1da851] text-white gap-1.5"
              data-testid="campaign-start-btn">
              <Play size={14} weight="fill" /> Start
            </Button>
          )}
          {data.status === "paused" && (
            <Button size="sm" onClick={() => action("start")} disabled={actionLoading}
              className="bg-[#25D366] hover:bg-[#1da851] text-white gap-1.5"
              data-testid="campaign-resume-btn">
              <Play size={14} weight="fill" /> Resume
            </Button>
          )}
          {data.status === "running" && (
            <Button size="sm" variant="outline" onClick={() => action("pause")} disabled={actionLoading}
              className="gap-1.5" data-testid="campaign-pause-btn">
              <Pause size={14} weight="fill" /> Pause
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={exportCsv} className="gap-1.5"
            data-testid="campaign-export-btn">
            <DownloadSimple size={14} /> Export
          </Button>
          <Button size="sm" variant="outline" onClick={remove} disabled={actionLoading}
            className="gap-1.5 text-[#991B1B] border-[#FCA5A5] hover:bg-[#FEE2E2]"
            data-testid="campaign-delete-btn">
            <Trash size={14} />
          </Button>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard icon={<Clock />} label="Total" value={data.total} color="text-[var(--fg)]" testid="stat-total" />
        <StatCard icon={<CheckCircle weight="fill" />} label="Sent" value={data.sent} color="text-[#10B981]" testid="stat-sent" />
        <StatCard icon={<XCircle weight="fill" />} label="Failed" value={data.failed} color="text-[#EF4444]" testid="stat-failed" />
        <StatCard icon={<Clock weight="fill" />} label="Pending" value={data.pending} color="text-[#F59E0B]" testid="stat-pending" />
      </div>

      <div>
        <ProgressBar total={data.total} sent={data.sent} failed={data.failed} />
        <div className="text-[11px] text-[var(--muted)] mt-1 flex justify-between">
          <span>{data.total ? Math.round(((data.sent + data.failed) / data.total) * 100) : 0}% complete</span>
          {data.status === "running" && <span className="text-[#1E40AF] flex items-center gap-1">
            <CircleNotch size={10} className="animate-spin" /> Sending @ ~1 msg/sec
          </span>}
        </div>
      </div>

      {/* Recipients table */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="text-xs font-semibold uppercase tracking-wide">Recipients ({filteredRecipients.length})</div>
          <div className="flex items-center gap-1 text-xs ml-auto">
            {["all", "pending", "sent", "delivered", "read", "failed"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-2 py-0.5 rounded ${
                  filter === f ? "bg-[var(--fg)] text-[var(--bg)]" : "text-[var(--muted)] hover:bg-[var(--bg)]"
                }`}
                data-testid={`recipient-filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        <ScrollArea className="h-[40vh] border border-[var(--border)] rounded-md">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[var(--surface)] border-b border-[var(--border)]">
              <tr className="text-left">
                <th className="px-3 py-2 font-medium">Phone</th>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Params</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Sent at / Error</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecipients.map((r, i) => (
                <tr key={i} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg)]" data-testid={`recipient-row-${r.phone}`}>
                  <td className="px-3 py-2 font-mono">+{r.phone}</td>
                  <td className="px-3 py-2">{r.name || "—"}</td>
                  <td className="px-3 py-2 text-[var(--muted)] truncate max-w-[200px]">
                    {(r.params || []).join(" · ") || "—"}
                  </td>
                  <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                  <td className="px-3 py-2 text-[var(--muted)]">
                    {r.status === "failed" ? <span className="text-[#991B1B]">{r.error}</span> :
                     r.read_at ? (
                       <span className="flex items-center gap-1 text-[#3B82F6]">
                         <Checks size={12} weight="fill" />
                         Read {formatDistanceToNow(new Date(r.read_at), { addSuffix: true })}
                       </span>
                     ) :
                     r.delivered_at ? (
                       <span className="flex items-center gap-1 text-[#0EA5E9]">
                         <Checks size={12} weight="fill" />
                         Delivered {formatDistanceToNow(new Date(r.delivered_at), { addSuffix: true })}
                       </span>
                     ) :
                     r.sent_at ? formatDistanceToNow(new Date(r.sent_at), { addSuffix: true }) : "—"}
                  </td>
                </tr>
              ))}
              {filteredRecipients.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-[var(--muted)]">No recipients in this filter.</td></tr>
              )}
            </tbody>
          </table>
        </ScrollArea>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color, testid }) {
  return (
    <div className="border border-[var(--border)] rounded-md p-3 bg-[var(--surface)]" data-testid={testid}>
      <div className={`text-xl ${color} flex items-center gap-1.5`}>
        {icon}
        <span className="font-bold">{value}</span>
      </div>
      <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] mt-0.5">{label}</div>
    </div>
  );
}

export default function CampaignsDialog({ open, onOpenChange }) {
  const [view, setView] = useState("list"); // list | create | detail
  const [activeId, setActiveId] = useState(null);
  const [items, setItems] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/campaigns");
      setItems(r.data.items || []);
    } catch (_) {
      // silent
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setView("list");
    refresh();
  }, [open]);

  // Auto-refresh list every 4s if any running campaign
  useEffect(() => {
    if (!open || view !== "list") return;
    if (!items.some((c) => c.status === "running")) return;
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, view, items]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="campaigns-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone size={20} weight="fill" className="text-[#25D366]" />
            Bulk Campaigns
          </DialogTitle>
          <DialogDescription>Send approved WhatsApp templates to a CSV list of customers.</DialogDescription>
        </DialogHeader>

        {view === "list" && (
          <CampaignList
            items={items}
            refreshing={refreshing}
            onRefresh={refresh}
            onCreate={() => setView("create")}
            onOpen={(id) => { setActiveId(id); setView("detail"); }}
          />
        )}
        {view === "create" && (
          <CreateForm
            onBack={() => setView("list")}
            onCreated={(id) => { setActiveId(id); setView("detail"); refresh(); }}
          />
        )}
        {view === "detail" && activeId && (
          <CampaignDetail
            id={activeId}
            onBack={() => { setView("list"); refresh(); }}
            onChanged={refresh}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
